import { AppSpinner } from '@rhc-shared-components/app-spinner';
import * as Sentry from '@sentry/react';
import React from 'react';
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import './App.scss';
import {
  AlertComponent,
  AlertProvider,
} from '@rhc-shared-components/alert-component';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import {
  RHCAuthProvider,
  useRHCAuthContext,
} from '@rhc-shared-components/rhc-sso';
import { ControllerContextProvider } from '@rhc-shared-components/control-wrapper';
import { checkAndSendAnalytics } from '@rhc-shared-components/rhc-eddl';
import { RHCErrorPage } from '@rhc-shared-components/error-page';
import { getUserInfo } from './utils/fetch';
import { getOnboardingStatus, getRhpaConfig } from './api/hydra';
import Env from './utils/env';
import ComponentLoader from './components/General/ComponentLoader/ComponentLoader';
import AccountDetails from './components/AccountDetails/AccountDetails';
import { IOnBoardingStatusResponse } from './constants/apiconstants-hydra';
import { useApiError } from './hooks';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false,
      refetchIntervalInBackground: false,
      refetchInterval: false,
    },
  },
});

const AccountsSPARoutes = [
  Env.routePrefix + '/my-profile',
  Env.routePrefix + '/company-profile',
  Env.routePrefix + '/team-members',
  Env.routePrefix + '/agreements',
  Env.routePrefix + '/api-keys',
];

const AuthenticatedApp: React.FC = () => {
  const { isAuthenticated } = useRHCAuthContext();
  const { pathname } = useLocation();
  const { apiError } = useApiError();
  const {
    data: onboardingStatus,
    isLoading,
    error,
  } = useQuery<IOnBoardingStatusResponse, { status?: number }>({
    queryKey: ['onboardingStatus'],
    queryFn: ({ signal }) => getOnboardingStatus(signal),
    enabled: isAuthenticated,
  });

  React.useEffect(() => {
    if (!onboardingStatus) return;
    const ONBOARDING_ROUTE = Env.routePrefix + '/onboarding';
    const AGREEMENTS_ROUTE = Env.routePrefix + '/agreements';

    const isOnBoardingIncomplete = onboardingStatus?.status !== 'completed';

    // onboarding isn't completed
    if (isOnBoardingIncomplete) {
      // allow tnc and onboarding page
      if ([ONBOARDING_ROUTE, AGREEMENTS_ROUTE].indexOf(pathname) === -1) {
        const CONNECT_PORTAL_URL = Env.ConnectPortalUrls[Env.getEnvName()];
        window.location.href = CONNECT_PORTAL_URL + "account/onboarding";
      }
    }
  }, [onboardingStatus, pathname]);

  React.useEffect(() => {
    if (isAuthenticated && Env.getEnvName() === Env.EnvNames.PROD) {
      Sentry.setUser(getUserInfo());
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (isAuthenticated) {
      checkAndSendAnalytics({ spaName: 'account-spa' });
    }
  }, [isAuthenticated, pathname]);
  if (apiError || error) {
    return <RHCErrorPage errorStatus={error?.status || apiError?.status} />;
  }
  return isLoading || !isAuthenticated ? (
    <div className="app-content">
      <AppSpinner />
    </div>
  ) : (
    <ControllerContextProvider
      appKey="rhpa_config"
      configApi={getRhpaConfig}>
      <AlertProvider>
        <AlertComponent />
        <Switch>
          <Redirect from="/" to={Env.routePrefix + "/my-profile"} exact />
          <Redirect from={Env.routePrefix} to={Env.routePrefix + "/my-profile"} exact />
          <Route
            exact={true}
            path={AccountsSPARoutes}
          >
            <AccountDetails />
          </Route>
          <Route
            exact={true}
            path={Env.routePrefix + '/dashboard'}
          >
            <ComponentLoader componentName="DashboardContainer" />
          </Route>
          <Route
            exact={true}
            path={Env.routePrefix + "/onboarding"}
          >
            <ComponentLoader componentName="BuildPartnerOnboarding" />
          </Route>
          <Route
            render={() => (
              <RHCErrorPage errorStatus={404} />
            )}
          />
        </Switch>
      </AlertProvider>
    </ControllerContextProvider>
  );
};

const App: React.FC = () => (
    <QueryClientProvider client={queryClient}>
      <RHCAuthProvider>
        <BrowserRouter>
          <AuthenticatedApp/>
        </BrowserRouter>
        <div id="modal-div-container"/>
      </RHCAuthProvider>
    </QueryClientProvider>
);

export default App;
